import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDispatch } from "react-redux";
import DocumentView from "../../DocumentView";
import { ObservationPhotoEditor } from "../../ImageEditor";
import ObservationInfo from "../../ObservationInfo";
import ObservationPageTitle from "../../ObservationPageTitle";
import TopRightTitle from "../../TopRightTitle";
import { ObservationsEditable as OET } from "../ReportObservations";
import { REORDER_REPORT_OBSERVATION_TEST_PHOTOS } from "../../../../../../store/actions/report.actions";

export default function ObservationTests({ style, index, roomIndex, tests }) {
  const dispatch = useDispatch();
  const sensors = useSensors(useSensor(PointerSensor));

  const handleTestPhotosReorder = (testIndex, newPhotos) => {
    dispatch({
      type: REORDER_REPORT_OBSERVATION_TEST_PHOTOS,
      payload: {
        photos: newPhotos,
        testIndex,
        roomIndex,
        observationIndex: index,
      },
    });
  };

  return (
    <DocumentView style={style}>
      <div className="document-head" style={{ border: "none" }}>
        <header>
          <div className="grid-pg1">
            <div>
              <ObservationPageTitle />
            </div>
            <div className="grid-pg1-item2">
              <TopRightTitle />
            </div>
          </div>
        </header>
      </div>

      <ObservationInfo index={index} roomIndex={roomIndex} />

      <div className="doc-body">
        <div>
          <div className="comments-heading">
            <p>
              <strong>Tests</strong>
            </p>
            <span></span>
          </div>

          {tests.map((test, i) => (
            <div className="test-list" key={`tests-${i}`}>
              <table className="test-table">
                <tbody>
                  <tr>
                    <td>Test Type</td>
                    <td>Sample Type</td>
                    <td>Client Approval</td>
                  </tr>
                  <tr>
                    <td>
                      <OET
                        reduxKey="test.name"
                        ml="50"
                        oi={index}
                        ti={i}
                        ri={roomIndex}
                      >
                        {test.test.name}
                      </OET>
                    </td>
                    <td>
                      <OET
                        reduxKey="test_sample.name"
                        ml="50"
                        oi={index}
                        ti={i}
                        ri={roomIndex}
                      >
                        {test.test_sample.name}
                      </OET>
                    </td>
                    <td>
                      {test.transaction_status === "finished" ? "Yes" : "No"}
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className="test-table">
                <tbody>
                  <tr>
                    {test.sample_info?.map((info) => (
                      <td>{info.name}</td>
                    ))}
                  </tr>
                  <tr>
                    {test.transaction_status === "finished"
                      ? test.sample_info?.map((info, si) => (
                          <td>
                            <OET
                              reduxKey="test.sample_info"
                              ml="50"
                              oi={index}
                              ti={i}
                              ri={roomIndex}
                              si={si}
                            >
                              {info.pivot.value == null
                                ? "----"
                                : info.pivot.value}
                            </OET>
                          </td>
                        ))
                      : // null
                        test.sample_info?.map((info, si) => (
                          <td>
                            <OET
                              reduxKey="test.sample_info"
                              ml="50"
                              oi={index}
                              ti={i}
                              ri={roomIndex}
                              si={si}
                            >
                              {info.pivot.value == null
                                ? "----"
                                : info.pivot.value}
                            </OET>
                          </td>
                        ))}
                  </tr>
                </tbody>
              </table>
              {test.transaction_status === "finished" && (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => {
                    const { active, over } = event;
                    if (active.id !== over?.id) {
                      const oldIndex = test.images.findIndex(
                        (p) => p.id === active.id
                      );
                      const newIndex = test.images.findIndex(
                        (p) => p.id === over?.id
                      );
                      const newPhotos = arrayMove(
                        test.images,
                        oldIndex,
                        newIndex
                      );
                      handleTestPhotosReorder(i, newPhotos);
                    }
                  }}
                >
                  <SortableContext
                    items={test.images.map((p) => p.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="photos-grid">
                      {test.images.map((img, j) => (
                        <SortableTestPhotoCard
                          key={`test-img-${j}`}
                          id={img.id}
                          img={img}
                          j={j}
                          ri={roomIndex}
                          oi={index}
                          ti={i}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          ))}
        </div>
      </div>
    </DocumentView>
  );
}

function SortableTestPhotoCard({ id, img, j, ri, oi, ti }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "2px",
    boxShadow: isDragging ? "0 4px 20px rgba(0,0,0,0.2)" : "none",
    position: "relative",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {/* Drag Handle Icon */}
      <div
        {...listeners}
        style={{
          position: "absolute",
          top: "50%",
          left: "1px",
          transform: "translateY(-50%)",
          width: "20px",
          height: "20px",
          background: "rgb(240 240 240 / 42%)",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "grab",
          zIndex: 10,
          fontSize: "18px",
          color: "#74b44a",
        }}
        title="Drag to reorder"
      >
        <DragIndicatorIcon />
      </div>

      <ObservationPhotoEditor
        src={img.url}
        rotation={img.rotation ?? 0}
        alt=""
        className="test-photo-img"
        ri={ri}
        oi={oi}
        ti={ti}
        ii={j}
      />
    </div>
  );
}
